import React, { useState } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import {
  FiChevronLeft,
  FiHelpCircle,
  FiMail,
  FiMessageSquare,
  FiExternalLink,
  FiCreditCard,
  FiChevronDown,
} from 'react-icons/fi';
import { Header } from '../layout/Header';
import { BottomNav } from '../layout/BottomNav';

interface FAQItemProps {
  question: string;
  answer: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

const FAQItem = ({ question, answer, isOpen, onToggle }: FAQItemProps) => {
  return (
    <div className="border border-white/5 rounded-2xl overflow-hidden transition-colors duration-300">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 text-left bg-zinc-900/30 hover:bg-zinc-900/50 transition-colors"
      >
        <span className="text-white font-medium pr-4">{question}</span>
        <FiChevronDown
          size={20}
          className={`text-zinc-500 shrink-0 transition-transform duration-300 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>
      <div
        className="overflow-hidden transition-all duration-300 ease-out"
        style={{
          maxHeight: isOpen ? '600px' : '0px',
          opacity: isOpen ? 1 : 0,
        }}
      >
        <div className="p-5 pt-0 text-zinc-400 leading-relaxed">{answer}</div>
      </div>
    </div>
  );
};

const ContactCard = ({
  icon: Icon,
  title,
  description,
  action,
  href,
  color,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  action: string;
  href?: string;
  color: string;
}) => {
  const IconComponent = Icon;

  return (
    <a
      href={href}
      className="block p-6 rounded-3xl bg-zinc-900/40 border border-white/5 hover:border-white/10 transition-all hover:scale-[1.02] group"
    >
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center mb-4`}>
        <IconComponent size={24} />
      </div>
      <h3 className="text-white font-bold text-lg mb-2">{title}</h3>
      <p className="text-zinc-400 text-sm mb-4">{description}</p>
      <span className="inline-flex items-center gap-2 text-blue-400 font-medium text-sm group-hover:text-blue-300">
        {action}
        <FiExternalLink size={14} />
      </span>
    </a>
  );
};

export const HelpSupportPage: React.FC = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-24 md:pt-20 max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10">
          <nav className="flex items-center gap-2 text-zinc-500 text-[12px] font-medium tracking-wide">
            <Link to="/profile/settings" className="hover:text-white transition-colors">
              Settings
            </Link>
            <span className="text-zinc-800 font-light">/</span>
            <span className="text-zinc-200">Support</span>
          </nav>

          <button
            onClick={() => navigate({ to: '/profile/settings' })}
            className="flex items-center gap-2 text-zinc-400 hover:text-white transition-all group w-fit"
          >
            <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center group-hover:bg-white/10 transition-colors">
              <FiChevronLeft size={16} />
            </div>
            <span className="text-sm font-medium">Back to Settings</span>
          </button>
        </div>

        <div className="mb-12 relative">
          <div className="absolute -top-20 -left-20 w-64 h-64 bg-blue-500/5 blur-[120px] rounded-full pointer-events-none" />
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-purple-500/5 blur-[120px] rounded-full pointer-events-none" />

          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-white/10 flex items-center justify-center shadow-lg">
              <FiHelpCircle size={28} className="text-blue-400" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-white">
                Help & Support
              </h1>
              <p className="text-zinc-500 text-sm mt-1">We're here to help you succeed</p>
            </div>
          </div>
        </div>

        <section className="p-6 md:p-8 rounded-3xl bg-gradient-to-br from-zinc-900/60 to-zinc-800/40 border border-white/8 backdrop-blur-sm mb-8">
          <h2 className="text-white font-bold text-xl md:text-2xl mb-4">How Can We Help?</h2>
          <p className="text-zinc-400 leading-relaxed mb-6">
            Find answers to common questions below, or reach out to our support team directly. We're
            committed to helping you get the most out of Musicade.
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            <ContactCard
              icon={FiMail}
              title="Email Support"
              description="Get help from our team via email. We typically reply within 24 hours."
              action="Send Email"
              href="mailto:support@musicade.com"
              color="bg-blue-500/10 border border-blue-500/20 text-blue-400"
            />
            <ContactCard
              icon={FiCreditCard}
              title="Billing & Payments"
              description="Questions about charges, refunds, or payment methods?"
              action="Contact Billing"
              href="mailto:support@musicade.com"
              color="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400"
            />
          </div>
        </section>

        <section className="mb-8">
          <h2 className="text-white font-bold text-2xl mb-6 px-2">Frequently Asked Questions</h2>

          <div className="space-y-3">
            {[
              {
                question: "Why am I being asked to 'fill in all details correctly'?",
                answer: (
                  <>
                    This error appears when required fields are missing, such as:
                    <ul className="list-disc pl-5 mt-2 space-y-1 marker:text-zinc-600">
                      <li>Event description</li>
                      <li>Date & time</li>
                      <li>Location</li>
                    </ul>
                    <p className="mt-2">
                      Make sure all required fields are completed before proceeding.
                    </p>
                  </>
                ),
              },
              {
                question: 'Do I have to select a genre when creating an event?',
                answer:
                  "Yes. You must select at least one genre for your event. If you skip this step, you'll see an error prompting you to select one.",
              },
              {
                question: "Why can't I create a paid event?",
                answer:
                  "If you're creating a paid event, you must first set up your Stripe account. If Stripe is not connected, the system will prompt you to complete payment setup before continuing.",
              },
              {
                question: "Why can't I add tickets to my main event yet (multi-event)?",
                answer: (
                  <>
                    In multi-event creation, you must create at least one sub-event first. Once a
                    sub-event is added, you can then:
                    <ul className="list-disc pl-5 mt-2 space-y-1 marker:text-zinc-600">
                      <li>Add tickets to the main event</li>
                      <li>Add tickets to each sub-event</li>
                    </ul>
                  </>
                ),
              },
              {
                question: 'Can I create a multi-event without sub-events?',
                answer: (
                  <>
                    Yes. You can save the main event as a draft without sub-events. However, you
                    won&apos;t be able to:
                    <ul className="list-disc pl-5 mt-2 space-y-1 marker:text-zinc-600">
                      <li>Add main event tickets</li>
                      <li>Fully publish the multi-event</li>
                    </ul>
                    <p className="mt-2">until at least one sub-event is created.</p>
                  </>
                ),
              },
              {
                question: 'Can each sub-event have different tickets and schedules?',
                answer: (
                  <>
                    Yes. Each sub-event works like a mini-event, meaning it can have:
                    <ul className="list-disc pl-5 mt-2 space-y-1 marker:text-zinc-600">
                      <li>Its own tickets</li>
                      <li>Its own pricing</li>
                      <li>Its own date and time</li>
                    </ul>
                  </>
                ),
              },
              {
                question: "What's the difference between a Gig and a Service?",
                answer: (
                  <div className="space-y-3">
                    <p>
                      <span className="text-white font-medium">Gig:</span> A contractual job
                      opportunity posted by a user (e.g., hiring a DJ, booking a performer, looking
                      for a producer).
                    </p>
                    <p>
                      <span className="text-white font-medium">Service:</span> What you offer on
                      your profile (e.g., &ldquo;DJ for hire&rdquo;, &ldquo;Music
                      production&rdquo;).
                    </p>
                    <div className="pt-1 space-y-1">
                      <p>
                        <span className="text-white font-medium">Gigs</span> = demand (someone is
                        hiring)
                      </p>
                      <p>
                        <span className="text-white font-medium">Services</span> = supply (what you
                        offer)
                      </p>
                    </div>
                  </div>
                ),
              },
              {
                question: 'Can I apply for gigs using my profile?',
                answer:
                  'Yes. Your profile, services, and media act as your portfolio when engaging with gigs. Make sure your profile is complete to increase your chances of being selected.',
              },
              {
                question: 'Can I link my service or media to a gig?',
                answer: (
                  <>
                    Media and services are not directly &ldquo;attached&rdquo; to a gig, but they
                    support your profile. When someone views your profile from a gig:
                    <ul className="list-disc pl-5 mt-2 space-y-1 marker:text-zinc-600">
                      <li>They can see your services</li>
                      <li>They can view your media</li>
                    </ul>
                  </>
                ),
              },
              {
                question: 'Why is my service price showing a dollar ($) sign?',
                answer:
                  "Musicade automatically formats service prices in USD ($). You don't need to add the symbol manually—just enter the amount.",
              },
              {
                question: 'Can I add collaborators to media posts?',
                answer: (
                  <>
                    Yes. When uploading media, you can tag collaborators. These collaborator tags
                    are:
                    <ul className="list-disc pl-5 mt-2 space-y-1 marker:text-zinc-600">
                      <li>Clickable</li>
                      <li>Linked to their profiles</li>
                    </ul>
                  </>
                ),
              },
            ].map((item, i) => (
              <FAQItem
                key={i}
                question={item.question}
                answer={item.answer}
                isOpen={openIndex === i}
                onToggle={() => setOpenIndex(openIndex === i ? null : i)}
              />
            ))}
          </div>
        </section>

        <section className="p-6 md:p-8 rounded-3xl bg-gradient-to-br from-zinc-900/60 to-zinc-800/40 border border-white/8 backdrop-blur-sm">
          <h3 className="text-white font-bold text-xl mb-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
              <FiMessageSquare size={20} className="text-blue-400" />
            </div>
            Still Need Help?
          </h3>
          <p className="text-zinc-400 mb-6 leading-relaxed">
            Can't find what you're looking for? Our support team is here to help you with any
            questions or issues.
          </p>
          <a
            href="mailto:support@musicade.com"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-500 hover:bg-blue-600 text-white font-medium transition-all hover:scale-105"
          >
            <FiMail size={18} />
            Contact Support Team
          </a>
        </section>
      </main>

      <BottomNav />
    </div>
  );
};
